import { ChangeDetectorRef, Component, Input, OnChanges, OnInit, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgxEchartsDirective, provideEchartsCore } from 'ngx-echarts';
import * as echarts from 'echarts';
import { EChartsOption } from 'echarts';
import { FileApiService } from '@angular-workspace/services';
import { FormatService } from '../../../shared/services/format.service';

export interface VersionFile {
  name: string;
  size?: string;
  isSelected?: boolean;
}

/** MahaAgx theme primary color - matches var(--primary-color) */
const MAHAAGX_PRIMARY = '#00d084';
const MAHAAGX_PRIMARY_DARK = '#00b371';
const MAHAAGX_CHART_COLORS = ['#00d084', '#00b371', '#00ff9f', '#022118', '#033d2a', '#10b981'];

@Component({
  selector: 'app-dataset-visualisation',
  standalone: true,
  imports: [CommonModule, FormsModule, NgxEchartsDirective],
  providers: [provideEchartsCore({ echarts })],
  templateUrl: './dataset-visualisation.component.html',
  styleUrl: './dataset-visualisation.component.scss',
})
export class DatasetVisualisationComponent implements OnInit, OnChanges {
  @Input() datasetId: string | null = null;
  @Input() versionFiles: VersionFile[] = [];
  @Input() versionFilesTotalSize: string = '';
  @Input() versionFilesLastUpdated: string = '';
  @Input() license: string = '';

  chartType = 'bar';
  chartTypeLabel = 'Bar Chart';
  chartTypes: { key: string; label: string }[] = [
    { key: 'bar', label: 'Bar Chart' },
    { key: 'line', label: 'Line Chart' },
    { key: 'pie', label: 'Pie Chart' },
  ];
  metricTypes: string[] = ['sum', 'count', 'min', 'max', 'avg', 'std', 'var'];
  groupByColumns: string[] = [];
  selectedDimension = '';
  selectedSeries = '';
  selectedMetric = 'count';
  selectedColumnIsNumeric = false;
  selectedGroupByColumn = '';
  selectedFile: VersionFile | null = null;

  loading = false;
  loadingFiles = false;
  error = '';

  /** Effective file list: from parent or lazy-loaded via FileApiService */
  displayFiles: VersionFile[] = [];
  displayFilesTotalSize = '';
  displayFilesLastUpdated = '';

  tableHeaders: string[] = [];
  tableData: Record<string, unknown>[] = [];

  chartOptions: EChartsOption = {};

  /** File metadata from getFileMetadata API (for File Size and Last Updated display). */
  fileMetadata: any = null;
  fileMetadataLoading = false;

  private fileApiService = inject(FileApiService);
  private cdr = inject(ChangeDetectorRef);
  private formatService = inject(FormatService);

  ngOnInit(): void {
    this.syncDisplayFiles();
    if (this.datasetId && this.displayFiles.length === 0) {
      this.loadFilesFromApi();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['versionFiles'] || changes['versionFilesTotalSize'] || changes['versionFilesLastUpdated']) {
      this.syncDisplayFiles();
    }
    if (changes['datasetId'] && this.datasetId && this.displayFiles.length === 0) {
      this.loadFilesFromApi();
    }
  }

  /** Use parent-provided versionFiles or lazy-loaded files */
  private syncDisplayFiles(): void {
    if (this.versionFiles?.length > 0) {
      this.displayFiles = [...this.versionFiles];
      this.displayFilesTotalSize = this.versionFilesTotalSize;
      this.displayFilesLastUpdated = this.versionFilesLastUpdated;
    }
  }

  /** Lazy-load files when parent has not provided them (e.g. Visualisation tab opened first) */
  private async loadFilesFromApi(): Promise<void> {
    if (!this.datasetId) return;
    this.loadingFiles = true;
    this.error = '';
    this.cdr.detectChanges();

    try {
      const response = await this.fileApiService.getFilesForPreview(this.datasetId);
      const apiFiles = this.extractFilesFromResponse(response?.data);

      if (apiFiles.length > 0) {
        const filtered = apiFiles.filter((f: any) => f.isFile !== false);
        let totalSizeBytes = 0;
        let latestModified: Date | null = null;

        for (const f of filtered) {
          const size = f.size || 0;
          totalSizeBytes += size;
          if (f.lastModified) {
            const d = new Date(f.lastModified);
            if (!latestModified || d > latestModified) latestModified = d;
          }
        }

        this.displayFiles = filtered.map((f: any, i: number) => ({
          name: f.key || `file_${i + 1}`,
          size: (f.size || 0) > 0 ? this.formatFileSize(f.size || 0) : undefined,
          isSelected: false,
        }));

        this.displayFilesTotalSize = totalSizeBytes > 0 ? this.formatFileSize(totalSizeBytes) : '';
        this.displayFilesLastUpdated = latestModified ? this.formatService.formatDate(latestModified.toISOString()) : '';
      } else {
        this.displayFiles = [];
        this.displayFilesTotalSize = '';
        this.displayFilesLastUpdated = '';
        this.fileMetadata = null;
      }
    } catch (err: any) {
      this.error = 'Failed to load files: ' + (err?.message || 'Unknown error');
      this.displayFiles = [];
      this.fileMetadata = null;
      console.error('Error loading files for visualisation:', err);
    } finally {
      this.loadingFiles = false;
      this.cdr.detectChanges();
    }
  }

  private extractFilesFromResponse(data: any): any[] {
    if (!data || typeof data !== 'object') return [];
    if (Array.isArray(data.files)) return data.files;
    const inner = data.data;
    if (inner && typeof inner === 'object' && Array.isArray(inner.files)) return inner.files;
    return [];
  }

  private formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }

  onSelectedFileChange(file: VersionFile): void {
    this.selectedFile = file;
    this.loadPreview();
    this.loadFileMetadataForSelectedFile();
    this.scrollToTabView();
  }

  /** Scroll the page so the tab row (Dataset, Details, Visualisation, etc.) is in view, below the header. */
  private scrollToTabView(): void {
    setTimeout(() => {
      const tabsEl = document.getElementById('dataset-detail-tabs');
      if (!tabsEl) return;
      const headerOffset = 100; // space for fixed header (px)
      const rect = tabsEl.getBoundingClientRect();
      const scrollTop = window.scrollY ?? document.documentElement.scrollTop;
      const targetY = scrollTop + rect.top - headerOffset;
      window.scrollTo({ top: Math.max(0, targetY), behavior: 'smooth' });
    }, 0);
  }

  /** File size for selected file: from metadata (bytes → MB) or fallback to aggregate. */
  get selectedFileSizeDisplay(): string {
    if (this.fileMetadata?.size != null) {
      const bytes = typeof this.fileMetadata.size === 'string' ? parseInt(this.fileMetadata.size, 10) : this.fileMetadata.size;
      return !isNaN(bytes) ? this.formatFileSize(bytes) : this.displayFilesTotalSize;
    }
    return this.displayFilesTotalSize;
  }

  /** Last updated for selected file: from metadata (ISO → DD-MMM-YYYY HH:MM) or fallback. */
  get selectedFileLastUpdatedDisplay(): string {
    if (this.fileMetadata?.lastModified) {
      return this.formatService.formatDate(this.fileMetadata.lastModified) || this.displayFilesLastUpdated;
    }
    return this.displayFilesLastUpdated;
  }

  /** Load file metadata for the currently selected file. */
  async loadFileMetadataForSelectedFile(): Promise<void> {
    if (!this.selectedFile || !this.datasetId) {
      this.fileMetadata = null;
      this.cdr.detectChanges();
      return;
    }
    this.fileMetadataLoading = true;
    this.fileMetadata = null;
    this.cdr.detectChanges();
    try {
      const res = await this.fileApiService.getFileMetadata(this.datasetId, { key: this.selectedFile.name });
      const raw = res?.data;
      this.fileMetadata = raw?.success && raw?.data != null ? raw.data : raw;
    } catch (err) {
      console.warn('File metadata failed for', this.selectedFile.name, err);
      this.fileMetadata = null;
    } finally {
      this.fileMetadataLoading = false;
      this.cdr.detectChanges();
    }
  }

  async loadPreview(): Promise<void> {
    if (!this.selectedFile || !this.datasetId) {
      this.error = 'No file or dataset selected';
      this.cdr.detectChanges();
      return;
    }

    this.loading = true;
    this.error = '';

    try {
      const response = await this.fileApiService.generatePreview(this.datasetId, this.selectedFile.name);
      this.processPreviewData(response?.data);
    } catch (err: any) {
      this.loading = false;
      this.error = 'Failed to load preview: ' + (err?.message || 'Unknown error');
      console.error('Preview error:', err);
      this.cdr.detectChanges();
    }
  }

  private processPreviewData(previewData: any): void {
    this.loading = false;

    if (!previewData) {
      this.error = 'No preview data received';
      this.cdr.detectChanges();
      return;
    }

    // Handle various response structures
    let actualData = previewData;
    if (previewData?.data?.content !== undefined) {
      actualData = { content: previewData.data.content, format: previewData.data.format || previewData.format };
    } else if (previewData?.result?.[0]) {
      actualData = previewData.result[0];
    } else if (previewData?.success && previewData?.data) {
      actualData = previewData.data;
    }

    // content can be at actualData.content or directly in actualData (for csv: { headers, data })
    const content = actualData?.content || (actualData?.headers ? actualData : null);
    const format = (actualData?.format || '').toLowerCase();

    if (!content) {
      this.error = 'Invalid preview response structure. Expected format with headers and data.';
      this.cdr.detectChanges();
      return;
    }

    // API indicates preview not supported (e.g. "Error parsing JSON file") – do not plot
    if (content.previewSupported === false) {
      this.error = (content.message && typeof content.message === 'string') ? content.message : 'No preview found';
      this.tableHeaders = [];
      this.tableData = [];
      this.cdr.detectChanges();
      return;
    }

    switch (format) {
      case 'csv':
      case 'tsv':
        this.tableHeaders = content?.headers || [];
        this.tableData = content?.data || [];
        break;
      case 'xlsx':
        this.tableHeaders = content?.headers || [];
        this.tableData = content?.data || [];
        break;
      case 'parquet':
        this.tableHeaders = content?.headers || [];
        this.tableData = content?.data || [];
        break;
      case 'json':
      case 'xml':
        this.parseJsonContent(content);
        break;
      default:
        this.parseUnknownFormat(content);
    }

    if (this.tableHeaders.length > 0) {
      this.selectedDimension = this.tableHeaders[0];
      this.selectedColumnIsNumeric = this.isColumnNumeric(this.tableData, this.selectedDimension);
      if (this.selectedColumnIsNumeric) {
        this.metricTypes = ['sum', 'count', 'min', 'max', 'avg', 'std', 'var'];
        this.groupByColumns = this.getNonNumericColumns(this.tableData);
        this.selectedGroupByColumn = this.groupByColumns[0] || '';
      } else {
        this.metricTypes = ['count'];
        this.selectedMetric = 'count';
      }
      this.updateChartOptions();
    }

    this.cdr.detectChanges();
  }

  private parseJsonContent(content: any): void {
    const jsonData = content?.data ?? content;
    if (Array.isArray(jsonData) && jsonData.length > 0) {
      this.tableHeaders = Object.keys(jsonData[0] as object);
      this.tableData = jsonData;
    } else if (typeof jsonData === 'object' && jsonData !== null) {
      const arr = Array.isArray(jsonData) ? jsonData : [jsonData];
      this.tableHeaders = arr.length > 0 ? Object.keys(arr[0] as object) : [];
      this.tableData = arr;
    } else {
      this.tableHeaders = [];
      this.tableData = [];
    }
  }

  private parseUnknownFormat(content: any): void {
    if (content?.headers && Array.isArray(content?.data)) {
      this.tableHeaders = content.headers;
      this.tableData = content.data;
    } else if (Array.isArray(content) && content.length > 0 && typeof content[0] === 'object') {
      this.tableHeaders = Object.keys(content[0] as object);
      this.tableData = content;
    } else {
      this.error = 'Unsupported file format for visualisation';
      this.tableHeaders = [];
      this.tableData = [];
    }
  }

  onChartTypeChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    this.chartType = target.value;
    this.chartTypeLabel = this.chartTypes.find((c) => c.key === this.chartType)?.label || 'Bar Chart';
    if (this.chartTypeLabel === 'Line Chart') {
      const numericCols = this.getNumericColumnNames(this.tableData);
      this.selectedDimension = numericCols[0] || this.tableHeaders[0];
      this.selectedSeries = numericCols[1] || numericCols[0] || '';
    }
    this.updateChartOptions();
    this.cdr.detectChanges();
  }

  onDimensionChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    this.selectedDimension = target.value;
    this.selectedColumnIsNumeric = this.isColumnNumeric(this.tableData, this.selectedDimension);
    if (this.selectedColumnIsNumeric) {
      this.metricTypes = ['sum', 'count', 'min', 'max', 'avg', 'std', 'var'];
      this.groupByColumns = this.getNonNumericColumns(this.tableData);
      this.selectedGroupByColumn = this.groupByColumns[0] || '';
    } else {
      this.metricTypes = ['count'];
      this.selectedMetric = 'count';
    }
    this.updateChartOptions();
    this.cdr.detectChanges();
  }

  onSeriesChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    this.selectedSeries = target.value;
    this.updateChartOptions();
    this.cdr.detectChanges();
  }

  onMetricChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    this.selectedMetric = target.value;
    this.updateChartOptions();
    this.cdr.detectChanges();
  }

  onGroupByColumnChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    this.selectedGroupByColumn = target.value;
    this.updateChartOptions();
    this.cdr.detectChanges();
  }

  private getNumericColumnNames(data: Record<string, unknown>[]): string[] {
    if (!Array.isArray(data) || data.length === 0) return [];
    const firstRow = data[0];
    return Object.keys(firstRow).filter((key) => {
      const value = firstRow[key];
      return value !== '' && !isNaN(Number(value)) && isFinite(Number(value));
    });
  }

  private getNonNumericColumns(data: Record<string, unknown>[]): string[] {
    if (!Array.isArray(data) || data.length === 0) return [];
    return Object.keys(data[0]).filter(
      (col) =>
        !data.every((row) => {
          const value = row[col];
          return value === '' || (!isNaN(Number(value)) && isFinite(Number(value)));
        })
    );
  }

  private isColumnNumeric(data: Record<string, unknown>[], columnName: string): boolean {
    if (!Array.isArray(data) || data.length === 0) return false;
    return data.every((row) => {
      const value = row[columnName];
      return value === '' || (!isNaN(Number(value)) && isFinite(Number(value)));
    });
  }

  private getDistinctValues<T>(data: T[], key: keyof T): unknown[] {
    return Array.from(new Set(data.map((item) => item[key])));
  }

  private getCountsByColumn<T>(data: T[], key: keyof T): number[] {
    const counts: Record<string, number> = {};
    data.forEach((item) => {
      const val = item[key];
      if (val !== undefined) {
        counts[String(val)] = (counts[String(val)] || 0) + 1;
      }
    });
    return Object.entries(counts).map(([, count]) => count);
  }

  private groupBy<T>(data: T[], key: keyof T): Record<string, T[]> {
    return data.reduce((acc, row) => {
      const groupKey = String(row[key]);
      if (!acc[groupKey]) acc[groupKey] = [];
      acc[groupKey].push(row);
      return acc;
    }, {} as Record<string, T[]>);
  }

  private getSumByColumn<T>(data: T[], groupByCol: keyof T, valueCol: keyof T): number[] {
    const grouped = this.groupBy(data, groupByCol);
    return Object.values(grouped).map((rows) =>
      rows.reduce((sum, row) => sum + Number(row[valueCol]), 0)
    );
  }

  private getMinByColumn<T>(data: T[], groupByCol: keyof T, valueCol: keyof T): number[] {
    const grouped = this.groupBy(data, groupByCol);
    return Object.values(grouped).map((rows) =>
      Math.min(...rows.map((row) => Number(row[valueCol])))
    );
  }

  private getMaxByColumn<T>(data: T[], groupByCol: keyof T, valueCol: keyof T): number[] {
    const grouped = this.groupBy(data, groupByCol);
    return Object.values(grouped).map((rows) =>
      Math.max(...rows.map((row) => Number(row[valueCol])))
    );
  }

  private getAvgByColumn<T>(data: T[], groupByCol: keyof T, valueCol: keyof T): number[] {
    const grouped = this.groupBy(data, groupByCol);
    return Object.values(grouped).map((rows) => {
      const nums = rows.map((row) => Number(row[valueCol]));
      return nums.reduce((sum, n) => sum + n, 0) / nums.length;
    });
  }

  private getXYPairs(data: Record<string, unknown>[], xCol: string, yCol: string): [unknown, unknown][] {
    if (!Array.isArray(data) || !xCol || !yCol) return [];
    return data
      .filter((row) => Object.prototype.hasOwnProperty.call(row, xCol) && Object.prototype.hasOwnProperty.call(row, yCol))
      .map((row) => [row[xCol], row[yCol]]);
  }

  private getNameValuePairs(xAxis: unknown[], yAxis: number[]): { value: number; name: string }[] {
    return xAxis.map((name, i) => ({ value: yAxis[i], name: String(name) }));
  }

  private updateChartOptions(): void {
    if (!this.tableData?.length) return;
    this.tableHeaders = Object.keys(this.tableData[0] || {});

    if (this.chartTypeLabel === 'Bar Chart') {
      let xAxis: unknown[], yAxis: number[];

      switch (this.selectedMetric) {
        case 'sum':
          yAxis = this.getSumByColumn(this.tableData, this.selectedGroupByColumn as keyof (typeof this.tableData)[0], this.selectedDimension as keyof (typeof this.tableData)[0]);
          xAxis = this.getDistinctValues(this.tableData, this.selectedGroupByColumn as keyof (typeof this.tableData)[0]);
          break;
        case 'min':
          yAxis = this.getMinByColumn(this.tableData, this.selectedGroupByColumn as keyof (typeof this.tableData)[0], this.selectedDimension as keyof (typeof this.tableData)[0]);
          xAxis = this.getDistinctValues(this.tableData, this.selectedGroupByColumn as keyof (typeof this.tableData)[0]);
          break;
        case 'max':
          yAxis = this.getMaxByColumn(this.tableData, this.selectedGroupByColumn as keyof (typeof this.tableData)[0], this.selectedDimension as keyof (typeof this.tableData)[0]);
          xAxis = this.getDistinctValues(this.tableData, this.selectedGroupByColumn as keyof (typeof this.tableData)[0]);
          break;
        case 'avg':
          yAxis = this.getAvgByColumn(this.tableData, this.selectedGroupByColumn as keyof (typeof this.tableData)[0], this.selectedDimension as keyof (typeof this.tableData)[0]);
          xAxis = this.getDistinctValues(this.tableData, this.selectedGroupByColumn as keyof (typeof this.tableData)[0]);
          break;
        default:
          yAxis = this.getCountsByColumn(this.tableData, this.selectedDimension as keyof (typeof this.tableData)[0]);
          xAxis = this.getDistinctValues(this.tableData, this.selectedDimension as keyof (typeof this.tableData)[0]);
      }

      this.chartOptions = {
        tooltip: {},
        xAxis: {
          type: 'category',
          data: xAxis as string[],
          axisLabel: { rotate: (xAxis as string[]).length <= 13 ? 30 : 0, interval: (xAxis as string[]).length <= 13 ? 0 : 'auto' },
        },
        yAxis: { type: 'value' },
        series: [
          {
            name: 'Value',
            data: yAxis,
            type: 'bar',
            itemStyle: { color: MAHAAGX_PRIMARY, borderRadius: [5, 5, 0, 0] },
          },
        ],
      };
    } else if (this.chartTypeLabel === 'Line Chart') {
      const numericCols = this.getNumericColumnNames(this.tableData);
      this.selectedDimension = this.selectedDimension || numericCols[0];
      this.selectedSeries = this.selectedSeries || numericCols[1] || numericCols[0];
      const chartData = this.getXYPairs(this.tableData, this.selectedDimension, this.selectedSeries)
        .map(([x, y]) => [x ?? null, y ?? null] as [string | number | null, string | number | null]);

      this.chartOptions = {
        xAxis: { type: 'category' },
        yAxis: { type: 'value' },
        series: [
          {
            symbolSize: 12,
            data: chartData,
            type: 'line',
            smooth: true,
            itemStyle: { color: MAHAAGX_PRIMARY },
            lineStyle: { color: MAHAAGX_PRIMARY },
          },
        ],
      };
    } else if (this.chartTypeLabel === 'Pie Chart') {
      let xAxis: unknown[], yAxis: number[];
      switch (this.selectedMetric) {
        case 'sum':
          yAxis = this.getSumByColumn(this.tableData, this.selectedGroupByColumn as keyof (typeof this.tableData)[0], this.selectedDimension as keyof (typeof this.tableData)[0]);
          xAxis = this.getDistinctValues(this.tableData, this.selectedGroupByColumn as keyof (typeof this.tableData)[0]);
          break;
        case 'min':
        case 'max':
        case 'avg':
          yAxis = this.getAvgByColumn(this.tableData, this.selectedGroupByColumn as keyof (typeof this.tableData)[0], this.selectedDimension as keyof (typeof this.tableData)[0]);
          xAxis = this.getDistinctValues(this.tableData, this.selectedGroupByColumn as keyof (typeof this.tableData)[0]);
          break;
        default:
          yAxis = this.getCountsByColumn(this.tableData, this.selectedDimension as keyof (typeof this.tableData)[0]);
          xAxis = this.getDistinctValues(this.tableData, this.selectedDimension as keyof (typeof this.tableData)[0]);
      }
      const pieChartData = this.getNameValuePairs(xAxis, yAxis);

      this.chartOptions = {
        tooltip: { trigger: 'item' },
        legend: { show: false },
        series: [
          {
            name: this.selectedDimension,
            type: 'pie',
            radius: ['40%', '70%'],
            avoidLabelOverlap: false,
            label: { show: false },
            labelLine: { show: false },
            data: pieChartData,
            itemStyle: {
              color: (params: any) => MAHAAGX_CHART_COLORS[params.dataIndex % MAHAAGX_CHART_COLORS.length],
            },
          },
        ],
      };
    }
  }

  get supportedFormats(): string[] {
    return ['csv', 'tsv', 'xlsx', 'parquet', 'json'];
  }

  isFileSupportedForVisualisation(file: VersionFile): boolean {
    const ext = (file.name || '').split('.').pop()?.toLowerCase() || '';
    return this.supportedFormats.includes(ext);
  }
}
